import React, { useState } from 'react';
import { useSessionMedicationsDueQuery } from '../../store/api/localApi';
import { MedicationDueSlot } from '../../types';
import AdministerModal, { AdministerSubject } from './AdministerModal';

interface Props {
  sessionId: string;
  locationId: string;
  /** Child names for the kids in this session, keyed by student id. The board
   *  returns childId only, and a medication row without a name is unusable at
   *  the trolley. */
  childNamesById: Map<string, string>;
}

/** How each slot state reads to staff, and how loudly. OVERDUE is the only
 *  state that shouts — it is the one that means something is not yet done. */
const SLOT_STATE_LABELS: Record<MedicationDueSlot['state'], string> = {
  DUE: 'Due',
  GIVEN: 'Given',
  OVERDUE: 'Overdue',
  REFUSED: 'Refused',
  MISSED: 'Missed',
};

const SLOT_STATE_CLASSES: Record<MedicationDueSlot['state'], string> = {
  DUE: 'bg-white border-amber-300 hover:bg-amber-50',
  GIVEN: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
  OVERDUE: 'bg-rose-50 border-rose-300 hover:bg-rose-100',
  REFUSED: 'bg-slate-100 border-slate-300 hover:bg-slate-200',
  MISSED: 'bg-slate-100 border-slate-300 hover:bg-slate-200',
};

const SLOT_STATE_PILL: Record<MedicationDueSlot['state'], string> = {
  DUE: 'bg-amber-100 text-amber-800',
  GIVEN: 'bg-emerald-100 text-emerald-800',
  OVERDUE: 'bg-rose-100 text-rose-800',
  REFUSED: 'bg-slate-200 text-slate-700',
  MISSED: 'bg-slate-200 text-slate-700',
};

/**
 * States that already have an answer in the log, and so are not OUTSTANDING.
 *
 * They remain TAPPABLE. A resolved slot is not a closed one: a child who
 * vomits a dose back needs a second, and a slot marked missed in error needs
 * the real dose recorded. Disabling these rows would make the server's
 * DOUBLE_DOSE_NEEDS_OVERRIDE path unreachable from the UI — the requirement is
 * to WARN, not to block, and a UI that blocks is a UI where staff record the
 * second dose on paper instead.
 */
const RESOLVED_STATES: MedicationDueSlot['state'][] = ['GIVEN', 'REFUSED', 'MISSED'];

/**
 * The medications board inside a live session: what is due for the kids
 * actually in the room, plus the emergency medications they carry.
 *
 * ADDITIVE to RunSession — it renders above the roster and changes nothing
 * about check-in, pickup, observations or incidents.
 *
 * The two halves are deliberately different shapes, matching the server's
 * `{ slots, badges }` split (src/medications/utils/due-today.ts):
 *
 *   - SLOTS are scheduled doses. They are buttons; tapping one opens the
 *     administer modal prefilled for that dose.
 *   - BADGES are EMERGENCY_ONLY requirements. They are PASSIVE — no time, no
 *     state, nothing due. An EpiPen shown as "due at 08:00" trains staff to
 *     dismiss the very prompt that matters most, so a badge never appears in
 *     the slot list and never carries a slot testid. It is still tappable, but
 *     to a different destination: logging an EMERGENCY_USE, which is an
 *     emergency being reported, not a schedule being satisfied.
 */
const MedicationsDuePanel: React.FC<Props> = ({ sessionId, locationId, childNamesById }) => {
  const { data, isLoading } = useSessionMedicationsDueQuery(sessionId);
  const [subject, setSubject] = useState<AdministerSubject | null>(null);

  const slots = data?.slots ?? [];
  const badges = data?.badges ?? [];

  // Nothing due and nothing carried: render nothing rather than an empty
  // container. A permanent "no medications" box on a screen staff use all day
  // is noise that trains them to stop looking at this area.
  if (!isLoading && slots.length === 0 && badges.length === 0) return null;

  const outstanding = slots.filter((s) => !RESOLVED_STATES.includes(s.state)).length;

  return (
    <section
      data-testid="medications-due-panel"
      className="border border-slate-200 rounded-lg p-3 bg-white"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Medications today
        </h2>
        {outstanding > 0 && (
          <span className="text-xs font-medium text-rose-700" data-testid="medications-due-count">
            {outstanding} outstanding
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-xs text-slate-400" data-testid="medications-due-loading">
          Loading medications…
        </p>
      ) : (
        <>
          {slots.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {slots.map((slot) => {
                const childName = childNamesById.get(slot.childId) ?? 'Child';
                return (
                  <button
                    key={`${slot.requirementId}-${slot.slot}`}
                    type="button"
                    data-testid={`medication-due-item-${slot.requirementId}-${slot.slot}`}
                    onClick={() =>
                      setSubject({
                        requirementId: slot.requirementId,
                        medicationName: slot.medicationName,
                        dosage: slot.dosage,
                        childName,
                        slot: slot.slot,
                        medicationExpiredOn: slot.medicationExpiredOn,
                      })
                    }
                    className={`border rounded-md px-3 py-2 text-left transition disabled:cursor-default ${
                      slot.medicationExpiredOn && !RESOLVED_STATES.includes(slot.state)
                        ? 'bg-red-50 border-red-300 hover:bg-red-100'
                        : SLOT_STATE_CLASSES[slot.state]
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">{childName}</p>
                      <span
                        data-testid={`medication-due-state-${slot.requirementId}-${slot.slot}`}
                        className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                          slot.medicationExpiredOn && !RESOLVED_STATES.includes(slot.state)
                            ? 'bg-red-100 text-red-800'
                            : SLOT_STATE_PILL[slot.state]
                        }`}
                      >
                        {/* Expired outranks an UNRESOLVED state: the server
                            refuses a routine dose for this medicine, so
                            "Overdue — give it" would prompt staff into an
                            unresolvable refusal. A RESOLVED state keeps its
                            label — "this was given at 08:00" is the one fact
                            the board exists to convey — with the expiry
                            carried alongside as a marker. */}
                        {slot.medicationExpiredOn && !RESOLVED_STATES.includes(slot.state)
                          ? 'Expired'
                          : SLOT_STATE_LABELS[slot.state]}
                      </span>
                      {slot.medicationExpiredOn && RESOLVED_STATES.includes(slot.state) && (
                        <span
                          data-testid={`medication-due-expired-${slot.requirementId}-${slot.slot}`}
                          className="flex-shrink-0 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                        >
                          Expired
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 truncate">
                      {slot.medicationName} · {slot.dosage}
                    </p>
                    <p className="text-[11px] text-slate-500">{slot.slot}</p>
                  </button>
                );
              })}
            </div>
          )}

          {badges.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Emergency medication on site
              </p>
              <div className="flex flex-wrap gap-1.5">
                {badges.map((badge) => {
                  const childName = childNamesById.get(badge.childId) ?? 'Child';
                  return (
                    <button
                      key={badge.requirementId}
                      type="button"
                      data-testid={`emergency-badge-${badge.requirementId}`}
                      title={badge.emergencyInstructions}
                      onClick={() =>
                        setSubject({
                          requirementId: badge.requirementId,
                          medicationName: badge.medicationName,
                          // A badge carries no dose on the board, so the modal
                          // opens with the dose blank and the server falls back
                          // to the requirement's own dosage.
                          dosage: '',
                          childName,
                          isEmergency: true,
                          medicationExpiredOn: badge.medicationExpiredOn,
                        })
                      }
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-rose-300 bg-rose-50 text-[11px] font-medium text-rose-800 hover:bg-rose-100"
                    >
                      <span aria-hidden="true">🚨</span>
                      {childName} · {badge.medicationName}
                      {badge.medicationExpiredOn && (
                        <span
                          data-testid={`emergency-badge-expired-${badge.requirementId}`}
                          className="ml-0.5 rounded-full bg-red-600 px-1.5 text-[10px] font-semibold text-white"
                        >
                          Expired
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {subject && (
        <AdministerModal
          subject={subject}
          locationId={locationId}
          onClose={() => setSubject(null)}
        />
      )}
    </section>
  );
};

export default MedicationsDuePanel;
